const API_BASE =
  (process.env.REACT_APP_API_URL as string) ||
  'https://api.agentumi.xyz/api/insurance';

async function request<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${res.statusText}`);
  return res.json();
}

/* ── Product ────────────────────────────────────── */

export interface InsuranceProduct {
  id?: string;
  name: string;
  premium: number;
  coverage: number;
  maxPayout: number;
  status: 'active' | 'expired' | 'suspended';
  createdAt?: string;
}

export function createProduct(data: Omit<InsuranceProduct, 'id' | 'createdAt'>) {
  return request<InsuranceProduct>('/product', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function fetchProducts() {
  return request<InsuranceProduct[]>('/products');
}

/* ── Event ──────────────────────────────────────── */

export interface InsuranceEvent {
  id?: string;
  productId: string;
  agentId: string;
  type: 'failure' | 'timeout' | 'quality_issue' | 'slashing';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  createdAt?: string;
}

export function createEvent(data: Omit<InsuranceEvent, 'id' | 'createdAt'>) {
  return request<InsuranceEvent>('/event', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function fetchEvents() {
  return request<InsuranceEvent[]>('/events');
}

/* ── Compensation ───────────────────────────────── */

export interface Compensation {
  id?: string;
  eventId: string;
  amount: number;
  recipient: string;
  status: 'pending' | 'approved' | 'paid' | 'rejected';
  createdAt?: string;
}

export function createCompensation(data: Omit<Compensation, 'id' | 'createdAt'>) {
  return request<Compensation>('/compensation', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function fetchCompensations() {
  return request<Compensation[]>('/compensations');
}

/* ── Slashing ───────────────────────────────────── */

export interface Slashing {
  id?: string;
  agentId: string;
  amount: number;
  reason: string;
  createdAt?: string;
}

export function createSlashing(data: Omit<Slashing, 'id' | 'createdAt'>) {
  return request<Slashing>('/slashing', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function fetchSlashings() {
  return request<Slashing[]>('/slashings');
}

/* ── Aggregated Stats ───────────────────────────── */

export interface InsuranceStats {
  totalProducts: number;
  totalEvents: number;
  totalCompensations: number;
  totalSlashings: number;
  poolBalance: string;
}

export async function fetchInsuranceStats(): Promise<InsuranceStats> {
  try {
    const [products, events, compensations, slashings] = await Promise.all([
      fetchProducts(),
      fetchEvents(),
      fetchCompensations(),
      fetchSlashings(),
    ]);
    const premiums = products.reduce((s, p) => s + (p.premium || 0), 0);
    const payouts = compensations.reduce((s, c) => s + (c.amount || 0), 0);
    const slashed = slashings.reduce((s, sl) => s + (sl.amount || 0), 0);
    return {
      totalProducts: products.length,
      totalEvents: events.length,
      totalCompensations: compensations.length,
      totalSlashings: slashings.length,
      poolBalance: (premiums + slashed - payouts).toLocaleString(),
    };
  } catch {
    return {
      totalProducts: 0,
      totalEvents: 0,
      totalCompensations: 0,
      totalSlashings: 0,
      poolBalance: '0',
    };
  }
}
